import React, { useEffect, useState, useMemo } from "react";
import {
  Box, CssBaseline, Breadcrumbs, Link, Typography, Avatar, IconButton,
  Stack, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, InputAdornment, Tabs, Tab,
  CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, Snackbar, Alert, TableSortLabel
} from '@mui/material';
import {
  Search, CheckCircle, RadioButtonUnchecked, Sync, Phone,
  FileDownload, ReceiptOutlined, MenuBookOutlined
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import { saveAs } from 'file-saver';
import { pdf } from "@react-pdf/renderer";
import moment from "moment";

// Project Components & Assets
import Header from "../../component/header/Header";
import InvoicePDF from "./invoice";
import InvoiceBookPDF from "./invoiceBook";
import Invoice2PDF from "./invoice2";

const colors = {
  pageBg: '#F8F9FA',
  sidebarBg: '#F1F2F6',
  filterBg: "#FFF9E6",
  accent: '#FFC107',
  tableHeader: '#FDF7E2',
  textMain: '#5D4037'
};

const InvoiceListScreen = () => {
  const navigation = useNavigate();

  // --- State ---
  const [parentList, setParentList] = useState([]);
  const [parentIds, setParentIds] = useState([]);
  const [classList, setClassList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0 = Pending, 1 = Submitted
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('fullname');

  // Detail/Invoice States
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [tempDetailData, setTempDetailData] = useState(null);
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [invoiceType, setInvoiceType] = useState("Standard");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchInitialData();
    localStorage.setItem("current_tab", "Invoice");
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [parentRes, batchRes] = await Promise.all([
        fetch(`https://www.geniusmileniaeducation.link/db-api/parents`),
        fetch(`https://www.geniusmileniaeducation.link/db-api/batch`)
      ]);
      const parentJson = await parentRes.json();
      const batchJson = await batchRes.json();

      setParentList(parentJson.data || []);
      setParentIds(parentJson.data.map((val) => val.parentId));
      setClassList(batchJson.data || []);
    } catch (error) {
      setSnackbar({ open: true, message: "Connection Error", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmitStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/parents-submit/unsubmitted`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentIds: parentIds })
      });
      const json = await response.json();
      if (json.status === 1) {
        fetchInitialData()
        setSnackbar({ open: true, message: "Status reset successfully", severity: "success" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Reset failed", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const toggleSubmission = async (id, currentStatus) => {
    const newStatus = currentStatus === "submitted" ? "unsubmitted" : "submitted";
    try {
      const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/parents/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submit: newStatus })
      });
      const json = await response.json();
      if (json.status === 1) {
        setParentList(prev => prev.map(p => p.parentId === id ? { ...p, submit: newStatus } : p));
        setSnackbar({ open: true, message: "Status updated successfully", severity: "success" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Update failed", severity: "error" });
    }
  };

  const handleOpenInvoicePreview = async (id, type) => {
    setInvoiceType(type);
    setOpenInvoiceDialog(true);
    setLoadingDetail(true);
    try {
      const res = await fetch(`https://www.geniusmileniaeducation.link/db-api/parents/${id}`);
      const json = await res.json();
      setTempDetailData(json.data);
    } catch (error) {
      setSnackbar({ open: true, message: "Error loading details", severity: "error" });
    } finally {
      setLoadingDetail(false);
    }
  };

  const generatePDF = async () => {
    const isBook = invoiceType === "Book";
    const MyPDF = isBook ? InvoiceBookPDF : tempDetailData.location === "PD" ? Invoice2PDF : InvoicePDF;
    const fileName = `${tempDetailData.fullname} (${isBook ? 'BOOK' : 'INVOICE'} ${isBook ? '' : moment().add(1, 'month').format('MMMM')}).pdf`;

    const blob = await pdf(<MyPDF data={tempDetailData} classList={classList} />).toBlob();
    saveAs(blob, fileName);
  };

  const handleSortRequest = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // --- Universal Search & Filter Logic ---
  const filteredAndSortedData = useMemo(() => {
    const targetStatus = tabValue === 0 ? "unsubmitted" : "submitted";

    return parentList
      .filter((item) => {
        const searchStr = filterSearch.toLowerCase();
        const matchesTab = (item.submit || "unsubmitted") === targetStatus;
        const matchesSearch =
          item.fullname?.toLowerCase().includes(searchStr) ||
          item.mobileNo?.toLowerCase().includes(searchStr);
        return matchesTab && matchesSearch;
      })
      .sort((a, b) => {
        const aValue = a[orderBy] || "";
        const bValue = b[orderBy] || "";
        return order === 'asc' ? (aValue < bValue ? -1 : 1) : (bValue < aValue ? -1 : 1);
      });
  }, [parentList, filterSearch, tabValue, order, orderBy]);

  const columns = [
    { id: 'fullname', name: 'Parent Name' },
    { id: 'mobileNo', name: 'Contact Info' },
    { id: 'location', name: 'Location' },
    { id: 'payment', name: 'Payment Status' },
    { id: 'submit', name: 'Submission' }
  ];

  return (
    <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
      <CssBaseline />
      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

        <Breadcrumbs sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
          <Typography color="text.primary">Invoices</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Header title="Invoice Management" />
          <Button
            variant="contained"
            color="tertiary"
            onClick={handleResetSubmitStatus}
            sx={{ borderRadius: 2 }}
          >
            Reset List
          </Button>
        </Stack>

        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
            {/* <FilterList sx={{ color: colors.textMain }} /> */}
            <Tabs
              value={tabValue}
              onChange={(e, v) => setTabValue(v)}
              sx={{
                '& .MuiTabs-indicator': { bgcolor: colors.accent, height: 3 },
                '& .MuiTab-root': { fontWeight: 'bold', textTransform: 'none', color: colors.textMain }
              }}
            >
              <Tab label={`Pending (${parentList.filter(p => p.submit !== 'submitted').length})`} />
              <Tab label={`Submitted (${parentList.filter(p => p.submit === 'submitted').length})`} />
            </Tabs>

            <TextField
              fullWidth
              placeholder="Search by parent name or mobile..."
              size="small"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              sx={{ bgcolor: 'white', borderRadius: 2, maxWidth: 400 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            />
          </Stack>
        </Paper>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: '60vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id} sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSortRequest(column.id)}
                    >
                      {column.name}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>Generate</TableCell>
                <TableCell align="center" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}><CircularProgress size={30} /></TableCell></TableRow>
              ) : filteredAndSortedData.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}>No records found.</TableCell></TableRow>
              ) : filteredAndSortedData.map((parent) => (
                <TableRow key={parent.parentId} hover>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: colors.accent, color: 'black', fontSize: 14 }}>
                        {parent.fullname?.split(" ")[0]?.charAt(0)}{parent.fullname?.split(" ")[1]?.charAt(0)}
                      </Avatar>
                      <Typography variant="subtitle2" fontWeight="bold">{parent.fullname}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2">{parent.mobileNo || '-'}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={parent.location === "PD" ? "Pantai Dalam" : "PJCC"}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        bgcolor: parent.location === "PD" ? '#d1f6f9' : '#feffeb',
                        color: parent.location === "PD" ? '#2e6c7d' : '#c6a928',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={parent.payment?.toUpperCase() || "UNPAID"}
                      size="small"
                      sx={{
                        fontWeight: 'bold',
                        bgcolor: parent.payment === 'paid' ? '#E8F5E9' : '#FFEBEE',
                        color: parent.payment === 'paid' ? '#2E7D32' : '#C62828',
                        borderRadius: 1.5
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {parent.submit === "submitted" ?
                        <CheckCircle sx={{ color: '#2E7D32', fontSize: 18 }} /> :
                        <RadioButtonUnchecked sx={{ color: '#DDD', fontSize: 18 }} />
                      }
                      <Typography variant="body2">{parent.submit || "unsubmitted"}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Standard Invoice">
                        <IconButton size="small" onClick={() => handleOpenInvoicePreview(parent.parentId, "Standard")}>
                          <ReceiptOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Book Invoice">
                        <IconButton size="small" onClick={() => handleOpenInvoicePreview(parent.parentId, "Book")}>
                          <MenuBookOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Sync />}
                      onClick={() => toggleSubmission(parent.parentId, parent.submit)}
                      sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#DDD', color: 'black' }}
                    >
                      Status
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* --- Unified Invoice Preview Dialog --- */}
        <Dialog fullWidth maxWidth="sm" open={openInvoiceDialog} onClose={() => setOpenInvoiceDialog(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Print {invoiceType} Invoice</DialogTitle>
          <DialogContent dividers>
            {loadingDetail ? (
              <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
            ) : tempDetailData ? (
              <Box>
                <Typography fontWeight="bold" gutterBottom>Parent: {tempDetailData.fullname}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>Students Linked:</Typography>
                {tempDetailData.children?.map((child, i) => (
                  <Typography key={i} variant="body2" sx={{ ml: 1 }}>• {child.fullname} {invoiceType !== "Book" && `(RM${child.amount})`}</Typography>
                ))}
              </Box>
            ) : <Typography>No data available</Typography>}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenInvoiceDialog(false)} color="inherit">Close</Button>
            {!loadingDetail && tempDetailData && (
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={generatePDF}
                sx={{ bgcolor: colors.accent, color: 'black', fontWeight: 'bold', '&:hover': { bgcolor: '#e6ae00' } }}
              >
                Download PDF
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

      </Box>
    </Box>
  );
};

export default InvoiceListScreen;